/* ==========================================================================
   Mock database.

   This is the single source of demo data for the whole app. It exists so
   that when config.useMockServices flips to false, there is exactly one
   file to delete — no mock values are hardcoded anywhere else (not in
   components, not in HTML, not scattered across service files).

   Everything here is clearly fake: fake users, fake estates, fake meters,
   fake wallets, fake bills, fake transaction history. It is never
   presented to the user as live data — see the "Demo data" badge rendered
   by the UI whenever config.useMockServices is true.

   Two estates exist on purpose (Ilupeju Court and Lekki Gardens), same
   shape as real Supabase mode: every table row carries an estateId, and
   reads/writes are filtered by it. All three demo accounts belong to
   Ilupeju Court, exactly like a real resident or estate_admin would only
   ever belong to one — the demo super_admin account can still switch to
   view Lekki Gardens too, the same way a real one can, so the estate
   switcher has something real to demo without touching Supabase.
   ========================================================================== */

import type {
  AccessCode,
  AuthUser,
  Bill,
  Estate,
  Meter,
  MeterReading,
  Transaction,
  UsageEntry,
  UserRole,
} from "../../types";

interface MockUserRecord extends AuthUser {
  password: string; // plaintext, MOCK ONLY — a real backend hashes/salts and never returns this
}

/** Internal bookkeeping only — Transaction itself has no estateId field
 *  (real mode never maps that column back to the frontend either, it just
 *  filters by it server-side). Extending it here is safe: a
 *  StoredTransaction is structurally still a Transaction, callers that
 *  expect Transaction[] don't notice or care about the extra field. */
interface StoredTransaction extends Transaction {
  estateId: string;
}

let ESTATES: Estate[] = [
  {
    id: "estate_ilupeju_ct",
    name: "Ilupeju Court Estate",
    address: "14 Ilupeju Court Close, Lagos",
  },
  {
    id: "estate_lekki_gdn",
    name: "Lekki Gardens Estate",
    address: "22 Freedom Way, Lekki Phase 1, Lagos",
  },
];

const DEFAULT_ESTATE_ID = ESTATES[0]!.id;

const USERS: MockUserRecord[] = [
  {
    id: "usr_001",
    name: "Adaeze Okonkwo",
    email: "resident@propertyvest.demo",
    phone: "08031234567",
    password: "password123",
    role: "user",
    estateId: DEFAULT_ESTATE_ID,
    estateName: ESTATES[0]!.name,
    createdAt: "2026-06-02T09:00:00Z",
  },
  {
    id: "usr_002",
    name: "Tunde Bakare",
    email: "admin@propertyvest.demo",
    phone: "08059876543",
    password: "password123",
    role: "estate_admin",
    estateId: DEFAULT_ESTATE_ID,
    estateName: ESTATES[0]!.name,
    createdAt: "2026-04-11T09:00:00Z",
  },
  {
    id: "usr_003",
    name: "Yewande Coker",
    email: "owner@propertyvest.demo",
    phone: "08071122334",
    password: "password123",
    role: "super_admin",
    estateId: DEFAULT_ESTATE_ID,
    estateName: ESTATES[0]!.name,
    createdAt: "2026-01-05T09:00:00Z",
  },
];

let METERS: Meter[] = [
  {
    id: "mtr_001",
    serial: "04-1188-2201-7765",
    label: "Ilupeju Court, Block C",
    status: "active",
    sourceType: "grid",
    estateId: "estate_ilupeju_ct",
    linkedAt: "2026-06-03T10:00:00Z",
  },
  {
    id: "mtr_002",
    serial: "07-2249-1130-4482",
    label: "Ilupeju Court, Block D",
    status: "active",
    sourceType: "generator",
    estateId: "estate_ilupeju_ct",
    linkedAt: "2026-07-14T10:00:00Z",
  },
  {
    id: "mtr_003",
    serial: "09-3312-5567-8810",
    label: "Lekki Gardens Estate, Unit 12",
    status: "active",
    sourceType: "grid",
    estateId: "estate_lekki_gdn",
    linkedAt: "2026-08-20T10:00:00Z",
  },
];

const READINGS: Record<string, MeterReading> = {
  mtr_001: {
    meterId: "mtr_001",
    voltage: 231.4,
    amps: 6.2,
    powerFactor: 0.94,
    unitsRemainingKwh: 142.5,
    estimatedDaysRemaining: 6,
    lastSyncAt: "2026-09-20T09:58:00Z",
  },
  mtr_002: {
    meterId: "mtr_002",
    voltage: 219.8,
    amps: 8.7,
    powerFactor: 0.87,
    unitsRemainingKwh: 58.2,
    estimatedDaysRemaining: 3,
    lastSyncAt: "2026-09-20T09:41:00Z",
  },
  mtr_003: {
    meterId: "mtr_003",
    voltage: 227.1,
    amps: 4.4,
    powerFactor: 0.91,
    unitsRemainingKwh: 96.0,
    estimatedDaysRemaining: 9,
    lastSyncAt: "2026-09-20T08:15:00Z",
  },
};

const WALLETS: Record<string, number> = {
  estate_ilupeju_ct: 1_245_000, // ₦12,450.00
  estate_lekki_gdn: 500_000, // ₦5,000.00
};

let BILLS: Bill[] = [
  {
    id: "bill_001",
    estateId: "estate_ilupeju_ct",
    title: "Estate Service Charge — September",
    category: "Service Charge",
    amountKobo: 1_500_000,
    dueDate: "2026-09-28T00:00:00Z",
    status: "outstanding",
  },
  {
    id: "bill_002",
    estateId: "estate_ilupeju_ct",
    title: "Water Supply — September",
    category: "Water",
    amountKobo: 400_000,
    dueDate: "2026-09-25T00:00:00Z",
    status: "outstanding",
  },
  {
    id: "bill_003",
    estateId: "estate_ilupeju_ct",
    title: "Waste Management — August",
    category: "Waste",
    amountKobo: 250_000,
    dueDate: "2026-08-28T00:00:00Z",
    status: "paid",
    paidAt: "2026-08-24T14:12:00Z",
  },
  {
    id: "bill_004",
    estateId: "estate_lekki_gdn",
    title: "Estate Service Charge — September",
    category: "Service Charge",
    amountKobo: 1_800_000,
    dueDate: "2026-09-30T00:00:00Z",
    status: "outstanding",
  },
];

let TRANSACTIONS: StoredTransaction[] = [
  {
    id: "txn_003",
    estateId: "estate_ilupeju_ct",
    type: "wallet_funding",
    status: "success",
    amountKobo: 1_000_000,
    direction: "credit",
    description: "Wallet Funding",
    reference: "FND-A1B2C",
    createdAt: "2026-09-17T09:12:00Z",
  },
  {
    id: "txn_002",
    estateId: "estate_ilupeju_ct",
    type: "electricity_purchase",
    status: "success",
    amountKobo: 500_000,
    direction: "debit",
    description: "Electricity Purchase",
    reference: "VEND-C9D2E",
    createdAt: "2026-09-15T18:40:00Z",
    meterId: "mtr_001",
    meterSerial: "04-1188-2201-7765",
    units: "34.8 kWh",
  },
  {
    id: "txn_001",
    estateId: "estate_ilupeju_ct",
    type: "wallet_funding",
    status: "success",
    amountKobo: 750_000,
    direction: "credit",
    description: "Wallet Funding",
    reference: "FND-E4F1G",
    createdAt: "2026-09-12T08:05:00Z",
  },
  // Lekki Gardens starts with no transaction history at all — a genuinely
  // fresh estate, useful for exercising the empty-state UI honestly.
];

const NAIRA_PER_KWH_KOBO = 20_950; // ₦209.50/kWh, mock tariff for demo cost figures

const USAGE_HISTORY: UsageEntry[] = (
  [
    { date: "2026-09-14", meterId: "mtr_001", kwh: 9.4, sourceType: "grid" },
    { date: "2026-09-15", meterId: "mtr_001", kwh: 11.2, sourceType: "grid" },
    { date: "2026-09-16", meterId: "mtr_001", kwh: 7.8, sourceType: "generator" },
    { date: "2026-09-17", meterId: "mtr_001", kwh: 14.6, sourceType: "grid" },
    { date: "2026-09-18", meterId: "mtr_001", kwh: 13.1, sourceType: "grid" },
    { date: "2026-09-19", meterId: "mtr_001", kwh: 8.9, sourceType: "generator" },
    { date: "2026-09-20", meterId: "mtr_001", kwh: 10.3, sourceType: "grid" },
    { date: "2026-09-18", meterId: "mtr_003", kwh: 6.1, sourceType: "grid" },
    { date: "2026-09-19", meterId: "mtr_003", kwh: 5.4, sourceType: "grid" },
    { date: "2026-09-20", meterId: "mtr_003", kwh: 7.0, sourceType: "grid" },
  ] as const
).map((entry) => ({ ...entry, costKobo: Math.round(entry.kwh * NAIRA_PER_KWH_KOBO) }));

let txSeq = TRANSACTIONS.length;
function nextTxId(): string {
  txSeq += 1;
  return `txn_${String(txSeq).padStart(3, "0")}`;
}

let billSeq = BILLS.length;
function nextBillId(): string {
  billSeq += 1;
  return `bill_${String(billSeq).padStart(3, "0")}`;
}

let estateSeq = ESTATES.length;
function nextEstateId(): string {
  estateSeq += 1;
  return `estate_${String(estateSeq).padStart(3, "0")}`;
}

interface StoredAccessCode extends AccessCode {
  estateId: string;
}

let ACCESS_CODES: StoredAccessCode[] = [];
let accessCodeSeq = 0;

function nextAccessCodeId(): string {
  accessCodeSeq += 1;
  return `code_${String(accessCodeSeq).padStart(3, "0")}`;
}

function randomCode(): string {
  return String(Math.floor(10000000 + Math.random() * 90000000));
}

export const mockDb = {
  /** New signups with no invite code join this one, same as the real
   *  handle_new_user() trigger picks the oldest estate for now. */
  getDefaultEstateId(): string {
    return DEFAULT_ESTATE_ID;
  },

  getEstates(): Estate[] {
    return ESTATES;
  },

  getEstate(estateId: string): Estate | undefined {
    return ESTATES.find((e) => e.id === estateId);
  },

  createEstate(data: { name: string; address?: string }): Estate {
    const estate: Estate = { id: nextEstateId(), name: data.name, address: data.address };
    ESTATES = [...ESTATES, estate];
    WALLETS[estate.id] = 0;
    return estate;
  },

  findUserByEmail(email: string): MockUserRecord | undefined {
    return USERS.find((u) => u.email.toLowerCase() === email.toLowerCase());
  },

  createUser(data: { name: string; email: string; phone: string; password: string; role?: UserRole }): MockUserRecord {
    const record: MockUserRecord = {
      id: `usr_${String(USERS.length + 1).padStart(3, "0")}`,
      name: data.name,
      email: data.email,
      phone: data.phone,
      password: data.password,
      role: data.role ?? "user",
      estateId: DEFAULT_ESTATE_ID,
      estateName: ESTATES[0]!.name,
      createdAt: new Date().toISOString(),
    };
    USERS.push(record);
    return record;
  },

  updateUserRole(email: string, newRole: UserRole): MockUserRecord | undefined {
    const record = USERS.find((u) => u.email.toLowerCase() === email.toLowerCase());
    if (!record) return undefined;
    record.role = newRole;
    return record;
  },

  toAuthUser(record: MockUserRecord): AuthUser {
    const { password: _password, ...user } = record;
    return user;
  },

  getMeters(estateId: string): Meter[] {
    return METERS.filter((m) => m.estateId === estateId);
  },

  getReading(meterId: string): MeterReading | undefined {
    return READINGS[meterId];
  },

  linkMeter(meter: Meter): void {
    METERS = [...METERS, meter];
  },

  unlinkMeter(meterId: string): void {
    METERS = METERS.filter((m) => m.id !== meterId);
    delete READINGS[meterId];
  },

  getWalletBalanceKobo(estateId: string): number {
    return WALLETS[estateId] ?? 0;
  },

  creditWallet(estateId: string, amountKobo: number): number {
    WALLETS[estateId] = (WALLETS[estateId] ?? 0) + amountKobo;
    return WALLETS[estateId]!;
  },

  debitWallet(estateId: string, amountKobo: number): number {
    WALLETS[estateId] = (WALLETS[estateId] ?? 0) - amountKobo;
    return WALLETS[estateId]!;
  },

  getBills(estateId: string): Bill[] {
    return BILLS.filter((b) => b.estateId === estateId);
  },

  createBill(bill: Omit<Bill, "id" | "status" | "paidAt">): Bill {
    const full: Bill = { ...bill, id: nextBillId(), status: "outstanding" };
    BILLS = [full, ...BILLS];
    return full;
  },

  markBillPaid(billId: string): Bill | undefined {
    BILLS = BILLS.map((b) =>
      b.id === billId ? { ...b, status: "paid" as const, paidAt: new Date().toISOString() } : b
    );
    return BILLS.find((b) => b.id === billId);
  },

  getTransactions(estateId: string): Transaction[] {
    return TRANSACTIONS.filter((t) => t.estateId === estateId);
  },

  getUsageHistory(meterId: string): UsageEntry[] {
    return USAGE_HISTORY.filter((e) => e.meterId === meterId);
  },

  addTransaction(
    estateId: string,
    tx: Omit<Transaction, "id" | "createdAt"> & { createdAt?: string }
  ): Transaction {
    const full: StoredTransaction = {
      ...tx,
      estateId,
      id: nextTxId(),
      createdAt: tx.createdAt ?? new Date().toISOString(),
    };
    TRANSACTIONS = [full, ...TRANSACTIONS];
    return full;
  },

  getAccessCodes(estateId: string): AccessCode[] {
    return ACCESS_CODES.filter((c) => c.estateId === estateId).map(({ estateId: _e, ...rest }) => rest);
  },

  createAccessCode(estateId: string, data: Omit<AccessCode, "id" | "status" | "createdAt" | "code">): AccessCode {
    const full: StoredAccessCode = {
      ...data,
      estateId,
      id: nextAccessCodeId(),
      code: randomCode(),
      status: "pending",
      createdAt: new Date().toISOString(),
    };
    ACCESS_CODES = [full, ...ACCESS_CODES];
    const { estateId: _e, ...rest } = full;
    return rest;
  },
};
